import { useState, useRef, useEffect } from 'react';
import { NavLink, Link, Outlet, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  LogOut, LayoutDashboard, Sparkles, MessageSquare, BarChart3, Users,
  Building2, Bell, BookOpenCheck, History, UserCircle, Menu, X,
  GraduationCap, Zap, ChevronRight, Sun, Moon, Globe, Check,
} from 'lucide-react';

import { useTranslation } from 'react-i18next';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { useThemeStore, useLangStore, LANGUAGES } from '@/lib/theme';
import i18n from '@/lib/i18n';
import { Wordmark } from './Wordmark';
import { cn } from '@/lib/cn';

const NAV_BY_ROLE = {
  super_admin: [
    { to: '/dashboard',     label: 'nav.overview',   icon: LayoutDashboard },
    { to: '/admin/isds',    label: 'nav.districts',  icon: Building2 },
    { to: '/admin/users',   label: 'nav.users',      icon: Users },
    { to: '/teacher-suite', label: 'nav.aiTools',    icon: Sparkles },
    { to: '/analytics',     label: 'nav.analytics',  icon: BarChart3 },
  ],
  isd_admin: [
    { to: '/dashboard',   label: 'nav.district',   icon: LayoutDashboard },
    { to: '/admin/users', label: 'nav.staff',      icon: Users },
    { to: '/analytics',   label: 'nav.analytics',  icon: BarChart3 },
  ],
  school_admin: [
    { to: '/dashboard',     label: 'nav.school',    icon: LayoutDashboard },
    { to: '/teacher-suite', label: 'nav.aiTools',   icon: Sparkles },
    { to: '/admin/users',   label: 'nav.users',     icon: Users },
    { to: '/analytics',     label: 'nav.analytics', icon: BarChart3 },
  ],
  teacher: [
    { to: '/dashboard',             label: 'nav.dashboard', icon: LayoutDashboard },
    { to: '/teacher-suite',         label: 'nav.aiTools',   icon: Sparkles },
    { to: '/classes',               label: 'nav.myClasses', icon: GraduationCap },
    { to: '/teacher-suite/history', label: 'nav.history',   icon: History },
  ],
  student: [
    { to: '/dashboard', label: 'nav.home',     icon: LayoutDashboard },
    { to: '/sparkbot',  label: 'nav.sparkbot', icon: MessageSquare },
    { to: '/learn',     label: 'nav.academy',  icon: BookOpenCheck },
  ],
  parent: [
    { to: '/dashboard', label: 'nav.myChild', icon: LayoutDashboard },
  ],
};

const ROLE_LABEL = {
  super_admin: 'Super Admin',
  isd_admin:   'ISD Admin',
  school_admin:'School Admin',
  teacher:     'Teacher',
  student:     'Student',
  parent:      'Parent',
};

const ROLE_PILL = {
  super_admin: 'bg-violet-400/25 text-violet-200',
  isd_admin:   'bg-blue-400/25 text-blue-200',
  school_admin:'bg-indigo-400/25 text-indigo-200',
  teacher:     'bg-orange-400/25 text-orange-200',
  student:     'bg-green-400/25 text-green-200',
  parent:      'bg-gray-400/25 text-gray-200',
};

function ThemeToggle() {
  const { theme, toggleTheme } = useThemeStore();
  const isDark = theme === 'dark';
  return (
    <button
      onClick={toggleTheme}
      title={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
      className={cn(
        'relative inline-flex items-center justify-center w-9 h-9 rounded-xl transition-all',
        isDark
          ? 'bg-white/10 text-yellow-300 hover:bg-white/15'
          : 'hover:bg-gray-100 text-gray-500 hover:text-purple-600',
      )}
    >
      {isDark ? <Sun size={16} /> : <Moon size={16} />}
    </button>
  );
}

function LanguagePicker() {
  const { lang, setLang } = useLangStore();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const { t } = useTranslation();
  const current = LANGUAGES.find((l) => l.code === lang) ?? LANGUAGES[0];

  useEffect(() => {
    function handleClick(e) {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const changeLanguage = (code) => {
    setLang(code);
    i18n.changeLanguage(code);
    document.documentElement.dir = code === 'ar' ? 'rtl' : 'ltr';
    setOpen(false);
  };

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen((v) => !v)}
        title="Change language"
        className={cn(
          'inline-flex items-center justify-center w-9 h-9 rounded-xl transition-all',
          open
            ? 'bg-purple-100 text-purple-700 dark:bg-white/10 dark:text-white'
            : 'hover:bg-gray-100 text-gray-500 hover:text-purple-600 dark:hover:bg-white/10 dark:text-white/50 dark:hover:text-white',
        )}
      >
        <Globe size={16} />
      </button>

      {open && (
        <div className="absolute right-0 top-11 z-50 w-52 rounded-2xl border border-gray-200 dark:border-[#2a2a2a] bg-white dark:bg-[#111111] shadow-xl shadow-black/60 overflow-hidden">
          <div className="px-3 py-2 border-b border-gray-100 dark:border-[#2a2a2a]">
            <p className="text-[11px] font-bold uppercase tracking-widest text-gray-400 dark:text-zinc-500">{t('common.language')}</p>
          </div>
          <div className="max-h-72 overflow-y-auto py-1">
            {LANGUAGES.map((l) => (
              <button
                key={l.code}
                onClick={() => changeLanguage(l.code)}
                className={cn(
                  'w-full flex items-center gap-3 px-3 py-2.5 text-sm transition-colors text-left',
                  l.code === lang
                    ? 'bg-purple-50 dark:bg-white/10 text-purple-700 dark:text-white font-semibold'
                    : 'text-gray-700 dark:text-white/60 hover:bg-gray-50 dark:hover:bg-white/5',
                )}
              >
                <span className="text-base leading-none">{l.flag}</span>
                <span className="flex-1 truncate">{l.label}</span>
                {l.code === lang && <Check size={13} className="text-purple-600 dark:text-purple-400 shrink-0" />}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function NotifBell() {
  const { data: items = [] } = useQuery({
    queryKey: ['notifications'],
    queryFn: async () => {
      try { return (await api.get('/api/v1/notifications/mine/')).data; }
      catch { return []; }
    },
    refetchInterval: 60_000,
  });
  const unread = items.filter((n) => !n.is_read).length;
  return (
    <Link
      to="/notifications"
      className="relative inline-flex items-center justify-center w-9 h-9 rounded-xl hover:bg-gray-100 text-gray-500 hover:text-purple-600 transition-colors"
    >
      <Bell size={18} />
      {unread > 0 && (
        <span className="absolute right-1 top-1 inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-orange-500 px-1 text-[10px] font-bold text-white">
          {unread > 9 ? '9+' : unread}
        </span>
      )}
    </Link>
  );
}

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const links = NAV_BY_ROLE[user?.role] ?? [];

  const initial1 = (user?.first_name?.[0] || user?.email?.[0] || '?').toUpperCase();
  const initial2 = (user?.last_name?.[0] || '').toUpperCase();
  const initials = initial1 + initial2;
  const displayName = user?.first_name
    ? `${user.first_name} ${user.last_name || ''}`.trim()
    : user?.email?.split('@')[0] || 'User';

  const sidebarContent = (
    <div className="flex flex-col h-full">
      {/* Logo row */}
      <div className="flex items-center justify-between px-5 pt-6 pb-5">
        <Link to="/dashboard" onClick={() => setSidebarOpen(false)}>
          <Wordmark size="md" variant="white" />
        </Link>
        <button
          className="lg:hidden p-1.5 rounded-lg text-purple-200 dark:text-white/50 hover:bg-white/10 dark:hover:bg-white/5 transition-colors"
          onClick={() => setSidebarOpen(false)}
        >
          <X size={18} />
        </button>
      </div>

      {/* User card */}
      <div className="px-4 pb-5">
        <div className="flex items-center gap-3 p-3 rounded-2xl bg-white/10 dark:bg-white/5 border border-white/10 dark:border-[#2a2a2a]">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-400 to-violet-600 text-white flex items-center justify-center text-sm font-bold shrink-0 shadow-lg shadow-purple-900/40">
            {initials || '?'}
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-white text-sm font-semibold truncate leading-tight">{displayName}</div>
            <span className={cn('inline-block mt-1 text-[11px] font-semibold px-2 py-0.5 rounded-full', ROLE_PILL[user?.role] ?? ROLE_PILL.parent)}>
              {ROLE_LABEL[user?.role] ?? user?.role}
            </span>
          </div>
        </div>
      </div>

      <div className="mx-4 border-t border-white/10 dark:border-[#2a2a2a] mb-3" />

      {/* Nav */}
      <nav className="flex-1 px-3 space-y-0.5 overflow-y-auto">
        {links.map((l) => (
          <NavLink
            key={l.to}
            to={l.to}
            end={l.to === '/dashboard'}
            onClick={() => setSidebarOpen(false)}
            className={({ isActive }) =>
              cn(
                'flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all duration-150',
                isActive
                  ? 'bg-white text-purple-900 dark:text-black shadow-md shadow-black/20'
                  : 'text-purple-100/90 dark:text-white/50 hover:bg-white/10 dark:hover:bg-white/5 hover:text-white',
              )
            }
          >
            {({ isActive }) => (
              <>
                <l.icon size={16} className={isActive ? 'text-purple-700 dark:text-black' : 'text-purple-300 dark:text-white/30'} />
                <span className="flex-1">{t(l.label)}</span>
                {isActive && <ChevronRight size={13} className="text-purple-400 dark:text-black/50" />}
              </>
            )}
          </NavLink>
        ))}
      </nav>

      {/* Bottom */}
      <div className="px-3 pt-3 pb-5 mt-3 border-t border-white/10 dark:border-[#2a2a2a] space-y-0.5">
        <Link
          to="/profile"
          onClick={() => setSidebarOpen(false)}
          className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium text-purple-200/80 dark:text-white/40 hover:bg-white/10 dark:hover:bg-white/5 hover:text-white transition-all"
        >
          <UserCircle size={16} className="text-purple-400 dark:text-white/30" />
          {t('nav.profileSettings')}
        </Link>
        <button
          onClick={() => { logout(); navigate('/login'); }}
          className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium text-purple-200/80 dark:text-white/40 hover:bg-red-500/20 dark:hover:bg-red-500/10 hover:text-red-300 transition-all"
        >
          <LogOut size={16} className="text-purple-400 dark:text-white/30" />
          {t('nav.signOut')}
        </button>
      </div>
    </div>
  );

  return (
    <div className="flex min-h-screen bg-gray-50 dark:bg-black">
      {/* Mobile overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-30 bg-black/60 backdrop-blur-sm lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={cn(
          'fixed inset-y-0 left-0 z-40 w-64 flex flex-col transition-transform duration-300 ease-in-out',
          'sn-sidebar bg-gradient-to-b from-[#170b38] via-[#1e0f52] to-[#251565]',
          'shadow-2xl shadow-purple-950/50 dark:shadow-black/80 dark:border-r dark:border-[#2a2a2a]',
          'lg:static lg:translate-x-0 lg:z-auto',
          sidebarOpen ? 'translate-x-0' : '-translate-x-full',
        )}
      >
        {sidebarContent}
      </aside>

      {/* Main */}
      <div className="flex min-w-0 flex-1 flex-col">
        <header className="sticky top-0 z-20 flex h-16 items-center justify-between border-b border-gray-200/80 dark:border-[#2a2a2a] bg-white/90 dark:bg-[#111111]/95 backdrop-blur-md px-4 lg:px-6">
          <div className="flex items-center gap-3">
            <button
              className="lg:hidden p-2 text-gray-500 hover:text-white dark:text-white/50 rounded-xl hover:bg-purple-50 dark:hover:bg-white/10 transition-colors"
              onClick={() => setSidebarOpen(true)}
            >
              <Menu size={20} />
            </button>
            <div className="hidden sm:flex items-center gap-2 text-sm text-gray-500 dark:text-white/40">
              <Zap size={13} className="text-orange-500" />
              {t('header.welcomeBack')}{' '}
              <strong className="text-gray-900 dark:text-white font-semibold">
                {user?.first_name || displayName}
              </strong>
            </div>
          </div>
          <div className="flex items-center gap-1.5">
            <ThemeToggle />
            <LanguagePicker />
            <NotifBell />
            <Link
              to="/profile"
              className="w-9 h-9 rounded-xl bg-gradient-to-br from-purple-500 to-violet-600 text-white flex items-center justify-center text-xs font-bold hover:shadow-md hover:shadow-purple-200 transition-all"
            >
              {initials || <UserCircle size={16} />}
            </Link>
          </div>
        </header>

        <main className="flex-1 p-4 lg:p-6 overflow-auto dark:bg-black">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
